import { useCallback, useMemo, useState, Dispatch, SetStateAction } from 'react';
import {
  getPreviousIndications,
  getAttributes,
  duplicateIndication,
  addStudyData
} from '../../../api/submission_details';
import { HttpResponse, getNctAndIdentifierAttributeIds } from '../constant';
import {
  Study,
  StudyAttribute,
  IndicationItem,
  IndicationPair,
  AttributeDefinition
} from '../types';

function getAttributeValueByAttributeId(study: Study, attributeId: number): string | undefined {
  const attr = study.attributes?.find(
    (a: StudyAttribute) => Number(a.attribute_id) === attributeId
  );
  const val = attr?.attribute_value;
  return val !== undefined && val !== null && val !== '' ? String(val) : undefined;
}

function getNormalizedIndicationText(item: IndicationItem | null | undefined): string | null {
  const text = item?.indication?.text;
  if (!text) return null;
  return text
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function getIndicationPairId(
  currItem: IndicationItem | null | undefined,
  prevItem: IndicationItem | null | undefined,
  fallbackId: number
): number {
  return (
    currItem?.indication?.text_id ??
    prevItem?.indication?.text_id ??
    currItem?.indication?.id ??
    prevItem?.indication?.id ??
    fallbackId
  );
}

/* eslint-disable no-unused-vars */
interface UseCompareVersionsParams {
  applno: string;
  submissionType: string;
  submissionNumber: string;
  center: string;
  documentId: string | undefined;
  user: { curator_name: string; curator_email: string } | null;
  currentIndications: IndicationItem[];
  refreshCurrentIndications: () => Promise<void>;
  onShowSnackbar: (message: string, isError?: boolean) => void;
  readOnly?: boolean;
}

interface UseCompareVersionsReturn {
  open: boolean;
  openComparison: () => Promise<void>;
  closeComparison: () => void;
  isLoadingPrevData: boolean;
  indicationPairs: IndicationPair[];
  handleCopyIndication: (prevItem: IndicationItem) => Promise<void>;
  handleCopyStudy: (prevStudy: Study, currentIndicationId: number) => Promise<void>;
  isCopying: boolean;
  copyingStudyId: string | number | null;
  pendingCopyIndication: IndicationItem | null;
  setPendingCopyIndication: Dispatch<SetStateAction<IndicationItem | null>>;
  nctIdAttributeId: number | null;
  identifierAttributeId: number | null;
}
/* eslint-enable no-unused-vars */

export default function useCompareVersions({
  applno,
  submissionType,
  submissionNumber,
  center,
  documentId,
  user,
  currentIndications,
  refreshCurrentIndications,
  onShowSnackbar,
  readOnly = false
}: UseCompareVersionsParams): UseCompareVersionsReturn {
  const [open, setOpen] = useState(false);
  const [previousIndicationsData, setPreviousIndicationsData] = useState<IndicationItem[]>([]);
  const [attributesList, setAttributesList] = useState<AttributeDefinition[]>([]);
  const [isLoadingPrevData, setIsLoadingPrevData] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [copyingStudyId, setCopyingStudyId] = useState<string | number | null>(null);
  const [pendingCopyIndication, setPendingCopyIndication] = useState<IndicationItem | null>(null);

  const openComparison = useCallback(async () => {
    if (currentIndications?.length === 0) return;
    setIsLoadingPrevData(true);
    setOpen(true);
    setPreviousIndicationsData([]);
    try {
      const [prevResponse, attrsResponse] = await Promise.all([
        getPreviousIndications(applno, submissionType, submissionNumber, center),
        getAttributes().catch(e => {
          console.warn('openComparison: could not load attributes for comparison', e);
          return { data: { Status: HttpResponse.ErrorOrUnknown } };
        })
      ]);
      const success = prevResponse?.data?.Success ?? prevResponse?.Success;
      if (success?.previous_submission && success?.indications) {
        setPreviousIndicationsData(success.indications);
      } else {
        setPreviousIndicationsData([]);
      }
      if (attrsResponse?.data?.Status === HttpResponse.OK && attrsResponse?.data?.Success) {
        setAttributesList(attrsResponse.data.Success);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load previous submission data';
      onShowSnackbar(message, true);
      setPreviousIndicationsData([]);
      // eslint-disable-next-line no-console
      console.error('openComparison: failed to load previous indications', e);
    } finally {
      setIsLoadingPrevData(false);
    }
  }, [
    applno,
    submissionType,
    submissionNumber,
    center,
    currentIndications?.length,
    onShowSnackbar
  ]);

  const closeComparison = useCallback(() => setOpen(false), []);

  const { nctIdAttributeId, identifierAttributeId } = useMemo(
    () => getNctAndIdentifierAttributeIds(attributesList),
    [attributesList]
  );

  const handleCopyIndication = useCallback(
    async (prevItem: IndicationItem) => {
      if (readOnly) {
        onShowSnackbar(
          'This submission is locked. Edit the source submission or change detection.',
          true
        );
        return;
      }
      if (!user || !prevItem?.indication?.id) return;
      setIsCopying(true);
      try {
        const response = await duplicateIndication({
          applno,
          submission_type: submissionType,
          submission_number: Number(submissionNumber),
          indicationId: prevItem.indication.id,
          center,
          document_id: documentId,
          ...user
        });
        const status = response?.data?.Status ?? response?.Status;
        if (status === HttpResponse.OK) {
          await refreshCurrentIndications();
          onShowSnackbar(
            'Indication copied to current submission. This may need to be added to later submissions; please check.'
          );
        } else {
          onShowSnackbar(response?.data?.Error ?? 'Failed to copy indication', true);
        }
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Failed to copy indication';
        onShowSnackbar(message, true);
      } finally {
        setIsCopying(false);
      }
    },
    [
      user,
      applno,
      submissionType,
      submissionNumber,
      center,
      documentId,
      refreshCurrentIndications,
      onShowSnackbar,
      readOnly
    ]
  );

  const handleCopyStudy = useCallback(
    async (prevStudy: Study, currentIndicationId: number) => {
      if (readOnly) {
        onShowSnackbar(
          'This submission is locked. Edit the source submission or change detection.',
          true
        );
        return;
      }
      if (!user || !prevStudy) return;
      setCopyingStudyId(prevStudy.id);
      try {
        const attributeNameToId =
          attributesList.length > 0
            ? Object.fromEntries(
                attributesList.map((a: AttributeDefinition) => [a.attribute_name, a.attribute_id])
              )
            : {};
        const mappedAttributes = (prevStudy.attributes ?? []).map((attr: StudyAttribute) => ({
          attribute_id: attributeNameToId[attr.attribute_name] ?? attr.attribute_id,
          attribute_value: attr.attribute_value
        }));
        const payload = {
          nct_id:
            prevStudy.nct_id ??
            (nctIdAttributeId != null
              ? getAttributeValueByAttributeId(prevStudy, nctIdAttributeId)
              : undefined),
          identifier:
            prevStudy.identifier ??
            (identifierAttributeId != null
              ? getAttributeValueByAttributeId(prevStudy, identifierAttributeId)
              : undefined),
          center: prevStudy.center,
          control: prevStudy.control,
          randomized: prevStudy.randomized,
          blinding: prevStudy.blinding,
          combined_study_count: prevStudy.combined_study_count,
          has_combined_study_data: prevStudy.has_combined_study_data,
          is_label_diff_from_clinical_posting: prevStudy.is_label_diff_from_clinical_posting,
          label_diff_from_clinical_posting_notes: prevStudy.label_diff_from_clinical_posting_notes,
          attributes: mappedAttributes,
          data_source: center,
          ...user
        };
        const subNum = Number(submissionNumber);
        const response = await addStudyData(
          applno,
          submissionType,
          subNum,
          currentIndicationId,
          payload
        );
        const status = response?.data?.Status ?? response?.Status;
        if (status === HttpResponse.OK) {
          await refreshCurrentIndications();
          onShowSnackbar(
            'Study copied to current indication. This may need to be added to later submissions; please check.'
          );
        } else {
          onShowSnackbar(response?.data?.Error ?? 'Failed to copy study', true);
        }
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Failed to copy study';
        onShowSnackbar(message, true);
      } finally {
        setCopyingStudyId(null);
      }
    },
    [
      user,
      applno,
      submissionType,
      submissionNumber,
      center,
      attributesList,
      nctIdAttributeId,
      identifierAttributeId,
      refreshCurrentIndications,
      onShowSnackbar,
      readOnly
    ]
  );

  const indicationPairs = useMemo<IndicationPair[]>(() => {
    const previousIndications = previousIndicationsData ?? [];
    const currentIndicationList = currentIndications ?? [];
    const pairs: IndicationPair[] = [];
    const matchedPrevIndexes = new Set<number>();

    const prevIndexesByTextId = new Map<number, number[]>();
    previousIndications.forEach((item: IndicationItem, index: number) => {
      const tid = item.indication?.text_id;
      if (tid == null) return;
      const existing = prevIndexesByTextId.get(tid) ?? [];
      prevIndexesByTextId.set(tid, [...existing, index]);
    });

    const prevIndexesByNormalizedText = new Map<string, number[]>();
    previousIndications.forEach((item: IndicationItem, index: number) => {
      const textKey = getNormalizedIndicationText(item);
      if (textKey == null) return;
      const existing = prevIndexesByNormalizedText.get(textKey) ?? [];
      prevIndexesByNormalizedText.set(textKey, [...existing, index]);
    });

    currentIndicationList.forEach((currItem: IndicationItem, currentIndex: number) => {
      const textId = currItem.indication?.text_id;
      const matchingPrevByTextId =
        textId != null
          ? prevIndexesByTextId
              .get(textId)
              ?.find((prevIndex: number) => !matchedPrevIndexes.has(prevIndex))
          : undefined;
      const textKey = getNormalizedIndicationText(currItem);
      const matchingPrevByText =
        textKey != null
          ? prevIndexesByNormalizedText
              .get(textKey)
              ?.find((prevIndex: number) => !matchedPrevIndexes.has(prevIndex))
          : undefined;
      const prevIndex = matchingPrevByTextId ?? matchingPrevByText;
      const prevItem = prevIndex != null ? previousIndications[prevIndex] : null;
      if (prevIndex != null) matchedPrevIndexes.add(prevIndex);
      pairs.push({
        indicationId: getIndicationPairId(currItem, prevItem, currentIndex),
        prevItem,
        currItem
      });
    });

    previousIndications.forEach((prevItem: IndicationItem, prevIndex: number) => {
      if (matchedPrevIndexes.has(prevIndex)) return;
      pairs.push({
        indicationId: getIndicationPairId(null, prevItem, currentIndicationList.length + prevIndex),
        prevItem,
        currItem: null
      });
    });

    return pairs;
  }, [currentIndications, previousIndicationsData]);

  return {
    open,
    openComparison,
    closeComparison,
    isLoadingPrevData,
    indicationPairs,
    handleCopyIndication,
    handleCopyStudy,
    isCopying,
    copyingStudyId,
    pendingCopyIndication,
    setPendingCopyIndication,
    nctIdAttributeId,
    identifierAttributeId
  };
}
